import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Search, ChevronLeft, ChevronRight, MapPin, Plus, Globe } from 'lucide-react';
import { fetchStates, fetchStatesCount, fetchCountriesForDropdown } from '../services/api.ts';
import './States.css';

interface State {
  id: number;
  name: string;
  code: string;
  country_id: number;
  country_name?: string;
  enable: boolean;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

const States: React.FC = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState<State[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [totalStates, setTotalStates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const statesPerPage = 12;

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data || []);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * statesPerPage;
        const [statesData, countData] = await Promise.all([
          fetchStates(offset, statesPerPage, filterCountry),
          fetchStatesCount(filterCountry)
        ]);
        setStates(statesData || []);
        setTotalStates(countData || 0);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load states');
        console.error('Error loading states:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStates();
  }, [currentPage, filterCountry]);

  const filteredStates = (states || []).filter((state) => {
    const matchesSearch = 
      (state.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (state.code || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && state.enable) ||
      (filterStatus === 'inactive' && !state.enable);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(totalStates / statesPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="states-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading states...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="states-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="states-page">
      <div className="page-header">
        <h2>States</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search states..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/states/new')}>
            <Plus size={18} />
            Add New State
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Country:</label>
          <select 
            value={filterCountry ?? ''} 
            onChange={(e) => {
              const value = e.target.value;
              setFilterCountry(value === '' ? undefined : parseInt(value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredStates.length} of {totalStates} states
          </span>
        </div>
      </div>

      {filteredStates.length === 0 ? (
        <div className="empty-state">
          <p>No states found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="states-table-container">
            <table className="states-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStates.map((state) => (
                  <tr key={state.id}>
                    <td className="cell-id">{state.id}</td>
                    <td className="cell-name">
                      <MapPin size={16} />
                      {state.name}
                    </td>
                    <td className="cell-code">{state.code}</td>
                    <td className="cell-country">
                      <Globe size={14} />
                      {state.country_name || `ID: ${state.country_id}`}
                    </td>
                    <td>
                      <span className={`status-badge ${state.enable ? 'active' : 'inactive'}`}>
                        {state.enable ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="cell-actions">
                      <button
                        className="edit-btn"
                        onClick={() => navigate(`/dashboard/states/edit/${state.id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default States;
